import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, LinearProgress, Typography, alpha } from '@mui/material';
import {
    CheckCircle as CheckIcon,
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Feedback as FeedbackIcon,
    FormatQuote as FormatQuoteIcon,
    Gavel as GavelIcon,
    Image as ImageIcon,
    Lock as LockIcon,
    Share as ShareIcon,
    Visibility as VisibilityIcon,
    PlayArrow as PlayArrowIcon,
    VideoLibrary as VideoIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ISubmission, IProject } from '../../types/global/helper';
import { IAwardPrize } from '../../redux/contest/types';
import { isEmpty } from '../../utils/lodash';
import { useDispatchPromise, showToastMessage } from '../../utils/AppUtils';
import { updateSubmission, rescindSubmission } from '../../redux/contest/actions';
import { createProject } from '../../redux/project/actions';
import { generateVideoSnapshot } from '../../components/VideoPlayer/VideoSnapshotGenerator';
import { base64ToFileWithDetails } from '../../utils/mediaHelper';
import { ICreateProject } from '../../redux/project/types';
import CustomLazyLoadImage from '../../components/Image/CustomLazyLoadImage';
import { RichTextViewer } from '../../components/Common/RichTextViewer';
import { DialogueSheet } from '../../components/DialogueSheet/DialogueSheet';
import { MixPanel, MIXPANEL_EVENT } from '../../analytics/Mixpanel';
import { setShareModalStatus } from '../../redux/appConfigurator/action';
import { openSubmissionPaymentCheckout } from '../../utils/openSubmissionPaymentCheckout';
import { api } from '../../configurations/api';
import { submissionsUrl } from '../../configurations/api/url';
import { httpRequest } from '../../redux/types';
import { ManageSubmissionHero } from './ManageSubmissionHero';
import { ROUTES } from '../../types/global/routes.types';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { HTTP_FORBIDDEN_CANONICAL, HTTP_FORBIDDEN_STATUS } from '../../constants/httpStatus';

function isSubmissionAccessDeniedError(error: unknown): boolean {
    const err = error as { status?: number; data?: { message?: string } };
    const status = Number(err?.status);
    if (status !== HTTP_FORBIDDEN_STATUS && status !== HTTP_FORBIDDEN_CANONICAL) {
        return false;
    }
    const message = String(err?.data?.message ?? '');
    return /access to this submission|do not have access|not authorized|permission/i.test(message) || !message;
}

export const ManageSubmission: React.FC = () => {
    const { themeStyle } = useThemeContext();
    const reduxDispatch = useDispatch();
    const dispatch = useDispatchPromise();
    const navigate = useNavigate();
    const location = useLocation();
    const { submissionId: routeSubmissionId } = useParams<{ submissionId: string }>();

    const [selectedSubmission, setSelectedSubmission] = useState<ISubmission | undefined>(undefined);
    const [loadingSubmission, setLoadingSubmission] = useState(!!routeSubmissionId);
    const [accessDenied, setAccessDenied] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
    const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null);
    const autoPayOpenedRef = useRef(false);

    /** Always verify with API — never trust location.state alone for access. */
    useEffect(() => {
        if (!routeSubmissionId) {
            setSelectedSubmission(undefined);
            setLoadingSubmission(false);
            setAccessDenied(false);
            setLoadError(false);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoadingSubmission(true);
            setAccessDenied(false);
            setLoadError(false);
            setSelectedSubmission(undefined);
            try {
                const res = await api(`${submissionsUrl}/${routeSubmissionId}`, httpRequest.GET, {}, 0, 0, false);
                const doc = (res?.data?.data ?? res?.data) as ISubmission | undefined;
                if (!doc?._id || cancelled) return;

                let payment_due = Boolean((doc as ISubmission).payment_due);
                let paid = Boolean((doc as ISubmission).paid);
                try {
                    const statusRes = await api(
                        `${submissionsUrl}/${routeSubmissionId}/payment-status`,
                        httpRequest.GET,
                        {},
                        0,
                        0,
                        false,
                    );
                    const status = statusRes?.data?.data ?? statusRes?.data;
                    if (status) {
                        payment_due = Boolean(status.payment_due);
                        paid = Boolean(status.paid);
                    }
                } catch {
                    // payment-status is best-effort; manage page still loads when access is ok
                }

                if (!cancelled) {
                    setSelectedSubmission({ ...doc, paid, payment_due });
                }
            } catch (error) {
                if (cancelled) return;
                setSelectedSubmission(undefined);
                if (isSubmissionAccessDeniedError(error)) {
                    setAccessDenied(true);
                } else {
                    setLoadError(true);
                    showToastMessage('Unable to load this submission.', 'error');
                }
            } finally {
                if (!cancelled) setLoadingSubmission(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [routeSubmissionId]);

    /** Email deep link: ?complete_payment=1 opens checkout once payment is due */
    useEffect(() => {
        if (autoPayOpenedRef.current || !selectedSubmission?._id || !selectedSubmission.payment_due) {
            return;
        }
        const params = new URLSearchParams(location.search);
        if (params.get('complete_payment') !== '1') return;

        autoPayOpenedRef.current = true;
        params.delete('complete_payment');
        const nextSearch = params.toString();
        navigate(
            { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
            { replace: true, state: location.state },
        );

        MixPanel.track(MIXPANEL_EVENT.ACTIVE_SUBMISSIONS_CLICK, {
            buttonAction: 'COMPLETE_SUBMISSION_PAYMENT',
            buttonName: 'MANAGE_SUBMISSION_PAY_DEEP_LINK',
            contestId: selectedSubmission.contest?._id,
            contestName: selectedSubmission.contest?.title,
        });
        void openSubmissionPaymentCheckout(reduxDispatch, selectedSubmission._id, {
            submission: selectedSubmission,
            contest: selectedSubmission.contest,
            project: selectedSubmission.project,
        });
    }, [selectedSubmission, location.pathname, location.search, location.state, navigate, reduxDispatch]);

    useEffect(() => {
        if (selectedSubmission?.testimonial_video && typeof selectedSubmission.testimonial_video === 'object') {
            const videoProject = selectedSubmission.testimonial_video as IProject;
            setVideoThumbnail(videoProject.thumbnail || videoProject.feature_image);
        }
    }, [selectedSubmission]);

    const judgeFeedbacks = useMemo(() => {
        const notes = selectedSubmission?.jury_notes;
        if (!Array.isArray(notes)) return [];
        return notes.map(note => (typeof note === 'string' ? note.trim() : '')).filter(note => note.length > 0);
    }, [selectedSubmission?.jury_notes]);

    const hasWonAward = useMemo(() => {
        if (!selectedSubmission) return false;
        return (
            ['winner', 'semi-finalist', 'finalist', 'second', 'third', 'fourth'].includes(
                selectedSubmission.judging_status,
            ) ||
            (selectedSubmission.awards && !isEmpty(selectedSubmission.awards))
        );
    }, [selectedSubmission]);

    const hasUploadedPhoto = !!selectedSubmission?.submission_picture;
    const hasUploadedVideo = !!(selectedSubmission?.testimonial_video as IProject)?._id;
    const isFullyCompleted = hasUploadedPhoto && hasUploadedVideo;

    const formatStatus = (awards?: IAwardPrize[]) => {
        if (!awards || awards.length === 0) return '';
        return awards
            .map(a => a.award_category?.category_title)
            .filter((t): t is string => Boolean(t))
            .join(' & ');
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !selectedSubmission?._id) return;
        setUploadingPhoto(true);
        dispatch(
            updateSubmission({
                submissionId: selectedSubmission._id,
                submission_picture: file,
            }),
        )
            .then((updatedSub: any) => {
                setSelectedSubmission(prev =>
                    prev ? { ...prev, submission_picture: updatedSub.submission_picture } : prev,
                );
                setUploadingPhoto(false);
            })
            .catch(() => {
                setUploadingPhoto(false);
            });
    };

    const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !selectedSubmission?._id) return;

        setUploadingVideo(true);
        const videoElement = document.createElement('video');
        videoElement.preload = 'metadata';
        videoElement.src = URL.createObjectURL(file);

        const videoTimeout = setTimeout(() => {
            setUploadingVideo(false);
            showToastMessage(
                'Video processing took too long. Try a different file or ensure device has enough memory.',
                'error',
            );
        }, 20000);

        videoElement.addEventListener('loadedmetadata', async () => {
            clearTimeout(videoTimeout);
            try {
                const metadata = {
                    duration: videoElement.duration,
                    width: videoElement.videoWidth,
                    height: videoElement.videoHeight,
                    type: file.type,
                    codec: '',
                };
                const thumbnail = await generateVideoSnapshot(videoElement.src, 2);
                setVideoThumbnail(thumbnail);
                const image = base64ToFileWithDetails(thumbnail).file;

                const projectPayload: ICreateProject = {
                    title: `Testimonial Video - ${selectedSubmission.project.title}`,
                    description: 'Testimonial video for award submission',
                    project_type: 'short',
                    media_type: 'video',
                    visible: false,
                    public_status: 'unlisted',
                    project_file: file,
                    feature_image: image,
                    project: '',
                    metadata,
                };

                dispatch(createProject(projectPayload))
                    .then((createdProject: any) =>
                        dispatch(
                            updateSubmission({
                                submissionId: selectedSubmission._id,
                                testimonial_video: createdProject._id,
                            }),
                        ),
                    )
                    .then((updatedSub: any) => {
                        setSelectedSubmission(prev =>
                            prev ? { ...prev, testimonial_video: updatedSub.testimonial_video } : prev,
                        );
                        setUploadingVideo(false);
                    })
                    .catch(() => setUploadingVideo(false));
            } catch {
                setUploadingVideo(false);
            } finally {
                URL.revokeObjectURL(videoElement.src);
            }
        });

        videoElement.addEventListener('error', () => {
            clearTimeout(videoTimeout);
            setUploadingVideo(false);
            showToastMessage('Error loading video. Please try a different file.', 'error');
            URL.revokeObjectURL(videoElement.src);
        });
    };

    const handleWithdraw = () => {
        if (!selectedSubmission) return;
        dispatch(rescindSubmission(selectedSubmission));
        setShowWithdrawConfirm(false);
        navigate(-1);
    };

    if (!selectedSubmission) {
        return (
            <Box
                sx={{
                    minHeight: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 3,
                    py: 6,
                    textAlign: 'center',
                    color: theme[themeStyle].tag_white,
                }}>
                {loadingSubmission ? (
                    <Box sx={{ width: '100%', maxWidth: 360 }}>
                        <Typography sx={{ mb: 2, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>Loading submission…</Typography>
                        <LinearProgress />
                    </Box>
                ) : accessDenied ? (
                    <>
                        <Box
                            sx={{
                                width: 88,
                                height: 88,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 3,
                                bgcolor: alpha(theme[themeStyle].true_black ?? '#000', 0.45),
                                border: `1px solid ${alpha(theme[themeStyle].tag_white, 0.18)}`,
                            }}>
                            <LockIcon sx={{ fontSize: 40, color: theme[themeStyle].highlight }} />
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 22, md: 28 },
                                mb: 1.5,
                            }}>
                            Access denied
                        </Typography>
                        <Typography
                            sx={{
                                maxWidth: 480,
                                mb: 3,
                                color: alpha(theme[themeStyle].tag_white, 0.72),
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                lineHeight: 1.5,
                            }}>
                            You don&apos;t have permission to view this submission. Only the person who submitted it (or
                            the contest host) can open this page.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/')}
                            sx={{
                                bgcolor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                borderRadius: 999,
                                px: 3,
                                fontWeight: 700,
                                '&:hover': { bgcolor: theme[themeStyle].highlight },
                            }}>
                            Go home
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography sx={{ mb: 2, maxWidth: 480, color: alpha(theme[themeStyle].tag_white, 0.8) }}>
                            {loadError
                                ? 'Unable to load this submission right now. Please try again later.'
                                : 'Submission details are unavailable. Open this page from Manage Submission, or use the link from your email.'}
                        </Typography>
                        <Button variant="contained" onClick={() => navigate(-1)}>
                            Go Back
                        </Button>
                    </>
                )}
            </Box>
        );
    }

    const participantName =
        (selectedSubmission as any)?.user?.firstName && (selectedSubmission as any)?.user?.lastName
            ? `${(selectedSubmission as any).user.firstName} ${(selectedSubmission as any).user.lastName}`
            : (selectedSubmission as any)?.user?.firstName ||
              selectedSubmission?.contest?.host?.name ||
              selectedSubmission?.project?.title ||
              'Creator';

    const heroImage = selectedSubmission.submission_picture || selectedSubmission.contest.feature_image;
    const testimonialProject = selectedSubmission.testimonial_video as IProject | undefined;
    const awardStatusLabel = formatStatus(selectedSubmission.awards);

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                color: theme[themeStyle].tag_white,
                px: { xs: 2, sm: 3, md: 4, lg: 5 },
                pt: 0,
                pb: { xs: 3, md: 5 },
                boxSizing: 'border-box',
                mb: 6,
                overflowX: 'hidden',
            }}>
            {/* Full-bleed hero — flush to top / side gutters (same pattern as contest detail) */}
            <Box
                sx={{
                    mx: { xs: -2, sm: -3, md: -4, lg: -5 },
                    width: {
                        xs: 'calc(100% + 32px)',
                        sm: 'calc(100% + 48px)',
                        md: 'calc(100% + 64px)',
                        lg: 'calc(100% + 80px)',
                    },
                }}>
                <ManageSubmissionHero
                    imageUrl={heroImage}
                    title={selectedSubmission.project.title}
                    contestTitle={selectedSubmission.contest?.title}
                    category={selectedSubmission.category}
                    awardLabel={awardStatusLabel || undefined}
                    paymentDue={Boolean(selectedSubmission.payment_due)}
                    judgingStatus={selectedSubmission.judging_status}
                    submittedAt={selectedSubmission.createdAt}
                    description={selectedSubmission.project?.description}
                    onBack={() => navigate(-1)}
                />
            </Box>

            <Box
                sx={{
                    maxWidth: 1200,
                    mx: 'auto',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    mt: { xs: 3, md: 4 },
                }}>
                {hasWonAward && (
                    <Card
                        sx={{
                            bgcolor: alpha(theme[themeStyle].true_black ?? '#000000', 0.55),
                            borderRadius: 4,
                            border: `1px solid ${alpha(theme[themeStyle].highlight, 0.2)}`,
                        }}>
                        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: 22, sm: 24, md: 34 },
                                    lineHeight: 1.08,
                                    letterSpacing: '-0.015em',
                                    wordBreak: 'break-word',
                                    color: theme[themeStyle].tag_white,
                                }}>
                                Congratulations,{' '}
                                <Box component="span" sx={{ color: theme[themeStyle].highlight }}>
                                    {participantName}.
                                </Box>
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 2,
                                    color: alpha(theme[themeStyle].tag_white, 0.8),
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: { xs: 13, sm: 14, md: 17 },
                                    lineHeight: 1.45,
                                    wordBreak: 'break-word',
                                }}>
                                Your submission has been recognized by the international jury. You are among the top 1%
                                of submissions this season.
                            </Typography>
                            <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<ShareIcon />}
                                    onClick={() => {
                                        const videoId = selectedSubmission.project?.video_id;
                                        const projectPath = videoId
                                            ? replaceRouteParam(ROUTES.PROJECT_DETAILS, 'slug', videoId)
                                            : `/project-details/${selectedSubmission.project?._id}`;
                                        reduxDispatch(
                                            setShareModalStatus({
                                                status: true,
                                                title: selectedSubmission.project.title,
                                                url: `${window.location.origin}${projectPath}`,
                                            }),
                                        );
                                    }}
                                    sx={{
                                        bgcolor: theme[themeStyle].highlight,
                                        color: theme[themeStyle].black,
                                        borderRadius: 999,
                                        px: 2.5,
                                        py: 1.5,
                                        fontWeight: 700,
                                        fontSize: { xs: 12, sm: 13, md: 14 },
                                        '& .MuiButton-startIcon': { color: theme[themeStyle].black },
                                        '&:hover': { bgcolor: theme[themeStyle].highlight },
                                    }}>
                                    Share Success
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {hasWonAward && (
                    <Card
                        sx={{
                            bgcolor: alpha(theme[themeStyle].true_black, 0.45),
                            borderRadius: 4,
                            border: `1px solid ${alpha(theme[themeStyle].highlight, 0.2)}`,
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 28px ${alpha(theme[themeStyle].true_black ?? '#000', 0.4)}`,
                            },
                        }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 2,
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 34,
                                        lineHeight: 1.1,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    Profile Status
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].highlight,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 28,
                                    }}>
                                    {isFullyCompleted ? '100%' : hasUploadedPhoto || hasUploadedVideo ? '50%' : '0%'}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    width: '100%',
                                    height: 10,
                                    bgcolor: theme[themeStyle].true_black ?? '#000',
                                    borderRadius: 999,
                                    overflow: 'hidden',
                                    mb: 3,
                                }}>
                                <Box
                                    sx={{
                                        width: isFullyCompleted
                                            ? '100%'
                                            : hasUploadedPhoto || hasUploadedVideo
                                            ? '50%'
                                            : '0%',
                                        height: '100%',
                                        bgcolor: theme[themeStyle].highlight,
                                        borderRadius: 999,
                                        transition: 'width 0.2s ease',
                                    }}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    mt: 1.25,
                                    mb: 2.5,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    lineHeight: 1.5,
                                }}>
                                Complete Your Profile
                                <br />
                                Upload your photo and testimonial video to complete your award profile.
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.8 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                    <CheckIcon
                                        sx={{
                                            color: hasUploadedPhoto
                                                ? theme[themeStyle].highlight
                                                : alpha(theme[themeStyle].tag_white, 0.4),
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: hasUploadedPhoto
                                                ? theme[themeStyle].tag_white
                                                : alpha(theme[themeStyle].tag_white, 0.55),
                                        }}>
                                        Upload award photo
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                    <CheckIcon
                                        sx={{
                                            color: hasUploadedVideo
                                                ? theme[themeStyle].highlight
                                                : alpha(theme[themeStyle].tag_white, 0.4),
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: hasUploadedVideo
                                                ? theme[themeStyle].tag_white
                                                : alpha(theme[themeStyle].tag_white, 0.55),
                                        }}>
                                        Upload testimonial video
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {hasWonAward && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: { xs: 28, md: 34 }, lineHeight: 1.1 }}>
                                Media Management
                            </Typography>
                            <Typography sx={{ color: alpha(theme[themeStyle].tag_white, 0.7), fontSize: 13 }}>
                                Max upload size: 500MB
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        aspectRatio: '16 / 9',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        border: `1px solid ${alpha(theme[themeStyle].tag_white, 0.18)}`,
                                        bgcolor: alpha(theme[themeStyle].true_black, 0.45),
                                    }}>
                                    {selectedSubmission.submission_picture ? (
                                        <CustomLazyLoadImage
                                            src={selectedSubmission.submission_picture}
                                            alt="Award"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <ImageIcon
                                                sx={{ color: alpha(theme[themeStyle].tag_white, 0.65), fontSize: 56 }}
                                            />
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1.5,
                                            bgcolor: 'rgba(0,0,0,0.24)',
                                        }}>
                                        <Button
                                            variant="contained"
                                            onClick={() =>
                                                selectedSubmission.submission_picture &&
                                                window.open(selectedSubmission.submission_picture, '_blank')
                                            }
                                            sx={{
                                                minWidth: 40,
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                p: 0,
                                                bgcolor: alpha(theme[themeStyle].true_black, 0.78),
                                                color: theme[themeStyle].tag_white,
                                            }}>
                                            <VisibilityIcon fontSize="small" />
                                        </Button>
                                        <Button
                                            component="label"
                                            variant="contained"
                                            sx={{
                                                minWidth: 40,
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                p: 0,
                                                bgcolor: alpha(theme[themeStyle].true_black, 0.78),
                                                color: theme[themeStyle].tag_white,
                                            }}>
                                            <UploadIcon fontSize="small" />
                                            <input type="file" hidden accept="image/*" onChange={handleFileUpload} />
                                        </Button>
                                    </Box>
                                </Box>

                                <Box sx={{ px: 0.5 }}>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, mb: 0.5 }}>
                                        Award Photo
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: 13, color: alpha(theme[themeStyle].tag_white, 0.72), mb: 1.5 }}>
                                        Primary asset for press releases and festival catalog.
                                    </Typography>
                                    <Button
                                        fullWidth
                                        component="label"
                                        variant="outlined"
                                        disabled={uploadingPhoto}
                                        startIcon={<UploadIcon />}
                                        sx={{
                                            py: 1.4,
                                            borderRadius: 2.5,
                                            borderStyle: 'dashed',
                                            borderWidth: 2,
                                            borderColor: alpha(theme[themeStyle].tag_white, 0.24),
                                            color: alpha(theme[themeStyle].tag_white, 0.92),
                                            fontWeight: 700,
                                            '&:hover': {
                                                borderColor: alpha(theme[themeStyle].highlight, 0.55),
                                                bgcolor: alpha(theme[themeStyle].highlight, 0.08),
                                            },
                                        }}>
                                        {uploadingPhoto
                                            ? 'Uploading...'
                                            : selectedSubmission.submission_picture
                                            ? 'Update Photo'
                                            : 'Upload Photo'}
                                        <input type="file" hidden accept="image/*" onChange={handleFileUpload} />
                                    </Button>
                                    {uploadingPhoto && <LinearProgress sx={{ mt: 1.25 }} />}
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        aspectRatio: '16 / 9',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        border: `1px solid ${alpha(theme[themeStyle].tag_white, 0.18)}`,
                                        bgcolor: alpha(theme[themeStyle].true_black, 0.45),
                                        cursor: testimonialProject?.project ? 'pointer' : 'default',
                                    }}
                                    onClick={() => {
                                        if (testimonialProject?.project)
                                            window.open(testimonialProject.project, '_blank');
                                    }}>
                                    {selectedSubmission.testimonial_video ? (
                                        <CustomLazyLoadImage
                                            src={
                                                videoThumbnail ||
                                                testimonialProject?.thumbnail ||
                                                testimonialProject?.feature_image
                                            }
                                            alt="Testimonial Thumbnail"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <VideoIcon
                                                sx={{ color: alpha(theme[themeStyle].tag_white, 0.65), fontSize: 56 }}
                                            />
                                        </Box>
                                    )}

                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                bgcolor: alpha(theme[themeStyle].highlight, 0.92),
                                                color: theme[themeStyle].true_black ?? '#000',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
                                            }}>
                                            <PlayArrowIcon sx={{ fontSize: 34 }} />
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 12,
                                            right: 12,
                                            bottom: 12,
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 1.5,
                                            bgcolor: 'rgba(0,0,0,0.62)',
                                            backdropFilter: 'blur(4px)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            fontSize: 12,
                                            fontWeight: 700,
                                        }}>
                                        <Typography sx={{ fontSize: 12, color: theme[themeStyle].tag_white }}>
                                            {testimonialProject?.title || 'Testimonial Video'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: theme[themeStyle].tag_white }}>
                                            {testimonialProject?.metadata?.duration
                                                ? `${Math.floor((testimonialProject.metadata.duration as number) / 60)
                                                      .toString()
                                                      .padStart(2, '0')}:${Math.floor(
                                                      (testimonialProject.metadata.duration as number) % 60,
                                                  )
                                                      .toString()
                                                      .padStart(2, '0')}`
                                                : '--:--'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ px: 0.5 }}>
                                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, mb: 0.5 }}>
                                        Testimonial Video
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: 13, color: alpha(theme[themeStyle].tag_white, 0.72), mb: 1.5 }}>
                                        A short video message from your director or peers.
                                    </Typography>
                                    <Button
                                        fullWidth
                                        component="label"
                                        variant="outlined"
                                        disabled={uploadingVideo}
                                        startIcon={<UploadIcon />}
                                        sx={{
                                            py: 1.4,
                                            borderRadius: 2.5,
                                            borderStyle: 'dashed',
                                            borderWidth: 2,
                                            borderColor: alpha(theme[themeStyle].tag_white, 0.24),
                                            color: alpha(theme[themeStyle].tag_white, 0.92),
                                            fontWeight: 700,
                                            '&:hover': {
                                                borderColor: alpha(theme[themeStyle].highlight, 0.55),
                                                bgcolor: alpha(theme[themeStyle].highlight, 0.08),
                                            },
                                        }}>
                                        {uploadingVideo
                                            ? 'Uploading...'
                                            : selectedSubmission.testimonial_video
                                            ? 'Upload New Video'
                                            : 'Upload Video'}
                                        <input type="file" hidden accept="video/*" onChange={handleVideoUpload} />
                                    </Button>
                                    {uploadingVideo && <LinearProgress sx={{ mt: 1.25 }} />}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FeedbackIcon sx={{ color: theme[themeStyle].highlight }} />
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: { xs: 24, md: 32 } }}>
                            Judges&apos; Perspective
                        </Typography>
                    </Box>

                    {judgeFeedbacks.length > 0 ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                            {judgeFeedbacks.map((feedback: any, index: number) => (
                                <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].highlight,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: { xs: 20, md: 26 },
                                            lineHeight: 1.05,
                                            px: 0.4,
                                            mb: 1,
                                        }}>
                                        Judge {index + 1}
                                    </Typography>
                                    <Card
                                        sx={{
                                            bgcolor: alpha(theme[themeStyle].true_black, 0.45),
                                            borderRadius: 3,
                                            borderLeft: `4px solid ${theme[themeStyle].highlight}`,
                                            overflow: 'visible',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: `0 12px 28px ${alpha(
                                                    theme[themeStyle].true_black ?? '#000',
                                                    0.4,
                                                )}`,
                                            },
                                        }}>
                                        <CardContent sx={{ p: 3, position: 'relative' }}>
                                            <FormatQuoteIcon
                                                sx={{
                                                    position: 'absolute',
                                                    top: -14,
                                                    left: -10,
                                                    fontSize: 34,
                                                    color: alpha(theme[themeStyle].highlight, 0.3),
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    color: alpha(theme[themeStyle].tag_white, 0.86),
                                                    fontStyle: 'italic',
                                                }}>
                                                <RichTextViewer content={feedback} textStyle={{ padding: 0 }} />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Card
                            sx={{
                                borderRadius: 3,
                                background: `linear-gradient(135deg, ${alpha(
                                    theme[themeStyle].deep_grey,
                                    0.95,
                                )} 0%, ${alpha(theme[themeStyle].true_black ?? '#000000', 0.95)} 100%)`,
                                border: `1px solid ${alpha('#484847', 0.28)}`,
                                boxShadow: '0 48px 96px rgba(0,0,0,0.8)',
                                overflow: 'hidden',
                                position: 'relative',
                            }}>
                            <CardContent
                                sx={{
                                    px: { xs: 3, md: 5 },
                                    py: { xs: 5, md: 8 },
                                    textAlign: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}>
                                <Box
                                    sx={{
                                        mb: 4,
                                        width: 96,
                                        height: 96,
                                        borderRadius: '50%',
                                        border: `1px solid ${alpha('#484847', 0.6)}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: theme[themeStyle].true_black ?? '#000000',
                                        boxShadow: `0 0 30px ${alpha(theme[themeStyle].highlight, 0.24)}`,
                                    }}>
                                    <GavelIcon
                                        sx={{
                                            fontSize: 44,
                                            color: theme[themeStyle].highlight,
                                        }}
                                    />
                                </Box>

                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: { xs: 24, md: 30 },
                                        lineHeight: 1.02,
                                        letterSpacing: '-0.03em',
                                        color: theme[themeStyle].tag_white,
                                        mb: 2,
                                    }}>
                                    No feedback yet.
                                </Typography>

                                <Typography
                                    sx={{
                                        color: alpha(theme[themeStyle].tag_white, 0.72),
                                        fontSize: { xs: 14, md: 18 },
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        lineHeight: 1.6,
                                        maxWidth: 680,
                                    }}>
                                    Judgement is still pending. Once reviewed, detailed feedback from the jury will
                                    appear here.
                                </Typography>

                                <Box
                                    sx={{
                                        mt: 6,
                                        width: 130,
                                        height: '1px',
                                        background: `linear-gradient(to right, transparent, ${alpha(
                                            '#484847',
                                            0.8,
                                        )}, transparent)`,
                                        opacity: 0.45,
                                    }}
                                />
                            </CardContent>
                        </Card>
                    )}
                </Box>

                {selectedSubmission.payment_due && (
                    <Card
                        sx={{
                            bgcolor: alpha(theme[themeStyle].primary_orange || '#f59e0b', 0.12),
                            borderRadius: 4,
                            color: theme[themeStyle].tag_white,
                            mb: 2,
                        }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ mb: 1, fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20 }}>
                                Payment Required
                            </Typography>
                            <Typography sx={{ mb: 2, opacity: 0.9 }}>
                                Your entry is submitted but unpaid. Complete payment to keep it eligible.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    MixPanel.track(MIXPANEL_EVENT.ACTIVE_SUBMISSIONS_CLICK, {
                                        buttonAction: 'COMPLETE_SUBMISSION_PAYMENT',
                                        buttonName: 'MANAGE_SUBMISSION_PAY',
                                        contestId: selectedSubmission.contest?._id,
                                        contestName: selectedSubmission.contest?.title,
                                    });
                                    void openSubmissionPaymentCheckout(reduxDispatch, selectedSubmission._id, {
                                        submission: selectedSubmission,
                                        contest: selectedSubmission.contest,
                                        project: selectedSubmission.project,
                                    });
                                }}>
                                Complete Payment
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {selectedSubmission.judging_status === 'submitted' &&
                    selectedSubmission.contest.contest_phase !== 'closed' && (
                        <Card
                            sx={{
                                bgcolor: alpha(theme[themeStyle].primary_red || '#ef4444', 0.08),
                                borderRadius: 4,
                                color: theme[themeStyle].tag_white,
                            }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ mb: 1, fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20 }}>
                                    Withdraw Submission
                                </Typography>
                                <Typography sx={{ mb: 2, opacity: 0.9 }}>
                                    Once withdrawn, you can resubmit to this campaign while it is still active.
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => setShowWithdrawConfirm(true)}>
                                    Withdraw Submission
                                </Button>
                            </CardContent>
                        </Card>
                    )}
            </Box>

            <DialogueSheet
                onClose={() => setShowWithdrawConfirm(false)}
                open={showWithdrawConfirm}
                onAgreeClicked={() => {
                    MixPanel.track(MIXPANEL_EVENT.PROCEED_WITH_RESCIND_SUBMISSION, {
                        buttonAction: 'PROCEED_WITH_RESCIND_SUBMISSION',
                        buttonName: 'PROCEED_WITH_RESCIND_SUBMISSION',
                    });
                    handleWithdraw();
                }}
                onDisagreeClick={() => setShowWithdrawConfirm(false)}
                header="Rescind Submission?"
                body="You are about to remove yourself from this campaign. This action cannot be reversed"
            />
        </Box>
    );
};
